import React, { useState } from 'react';

import type { UUID } from '@aline/common';
import { Select } from '@aline/common';
import { ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { CirclePlus, ClipboardList, Link2, ListTodo, Signature, SquarePlus } from 'lucide-react';
import { AlineButton } from 'src/components';
import { IconColors } from 'src/components/icons/icon-utils';
import { makeStyles } from 'tss-react/mui';

import { useAddPhase } from './use-add-phase';

export const usePhasesHeaderStyles = makeStyles()(theme => ({
  header: {
    alignItems: 'center',
    boxSizing: 'border-box',
    display: 'flex',
    gap: theme.spacing(1),
    width: '100%',
  },
  menuIcon: {
    color: theme.appColors.iconsType,
    minWidth: 'unset',
    paddingRight: theme.spacing(1.5),
  },
  menuPrimary: {
    color: theme.appColors.iconsType,
    fontSize: 14,
    fontWeight: 600,
  },
  menuSecondary: {
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  pillButton: {
    '&:hover': {
      borderColor: theme.appColors.primary,
      color: theme.appColors.primary,
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.white,
    border: '1px solid #d9d9d9',
    borderRadius: 48,
    color: '#708086',
    cursor: 'pointer',
    display: 'flex',
    fontSize: 12,
    fontWeight: 'bold',
    gap: theme.spacing(0.5),
    height: 24,
    padding: theme.spacing(0, 1),
  },
  title: {
    color: theme.appColors.iconsType,
    flex: 1,
    fontSize: 16,
    fontWeight: 'bold',
  },
}));

/** What the section's "Add" menu can add besides tasks, and where to report it. */
export interface PhaseAddOptions {
  canAddIntake: boolean;
  hasSigningPhase: boolean;
  onAddIntake: () => void;
  onLinkIntake: () => void;
  onPhaseCreated: (id: string) => void;
  /** With no phases there are no tasks to split yet, so "Add Phase" waits for the first task. */
  phaseCount: number;
  workflowId: UUID;
}

interface PhasesHeaderProps {
  addOptions: PhaseAddOptions;
  aiIsGenerating: boolean;
  /** Controls placed left of "Add", such as the task list's grouping. */
  actions?: React.ReactNode;
  /** Set on a phaseless list: "Add task" becomes the menu's first item. */
  onAddTask?: () => void;
  title?: string;
}

export function PhasesHeader({
  actions,
  addOptions,
  aiIsGenerating,
  onAddTask,
  title = 'Phases',
}: PhasesHeaderProps) {
  const { classes } = usePhasesHeaderStyles();

  return (
    <div className={classes.header}>
      <ListTodo stroke={IconColors.Primary} />
      <div className={classes.title}>{title}</div>
      {actions}
      {!aiIsGenerating && <PhaseAddMenu {...addOptions} onAddTask={onAddTask} />}
    </div>
  );
}

/**
 * Single "Add" control. It opens a dense menu while there is a choice to make
 * (a task on a phaseless list, a signing phase, an intake form on a template);
 * once only a plain phase is left it adds one on click.
 */
export function PhaseAddMenu({
  canAddIntake,
  hasSigningPhase,
  onAddIntake,
  onAddTask,
  onLinkIntake,
  onPhaseCreated,
  phaseCount,
  workflowId,
}: PhaseAddOptions & { onAddTask?: () => void }) {
  const { classes } = usePhasesHeaderStyles();
  const { addPhase } = useAddPhase(workflowId);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const handleAddPhase = async (isSigning: boolean) => {
    const id = await addPhase({ isSigning });
    if (id) onPhaseCreated(id);
  };

  const canAddPhase = phaseCount > 0;
  const canAddSigning = !hasSigningPhase;
  const collapseToAddPhase = !onAddTask && !canAddSigning && !canAddIntake;
  const closeMenuThen = (action: () => void) => () => {
    setAnchor(null);
    action();
  };

  return (
    <>
      <AlineButton
        buttonClassName={classes.pillButton}
        data-cy={Select.workflow.detail.phases.addPhaseMenuButton}
        size="small"
        startIcon={<CirclePlus size={16} />}
        onClick={event => {
          if (collapseToAddPhase) {
            void handleAddPhase(false);
            return;
          }
          setAnchor(event.currentTarget);
        }}
      >
        {collapseToAddPhase ? 'Add Phase' : 'Add'}
      </AlineButton>
      {/* Restoring focus to the button would blur the task field "Add task" just focused, and an empty field dismisses itself on blur. */}
      <Menu
        disableRestoreFocus
        anchorEl={anchor}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        open={Boolean(anchor)}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        onClose={() => setAnchor(null)}
      >
        {onAddTask && (
          <AddMenuItem
            icon={<SquarePlus size={18} />}
            primary="Add task"
            secondary="Add a task to this list"
            onClick={closeMenuThen(onAddTask)}
          />
        )}
        {canAddPhase && (
          <AddMenuItem
            icon={<ListTodo size={18} />}
            primary="Add Phase"
            secondary={onAddTask ? 'Split tasks into phases' : 'Create a phase with tasks'}
            onClick={closeMenuThen(() => void handleAddPhase(false))}
          />
        )}
        {canAddSigning && (
          <AddMenuItem
            dataCy={Select.workflow.detail.phases.addSigningPhaseButton}
            icon={<Signature size={18} />}
            primary="Add Signing Phase"
            secondary="Collect signatures from recipients"
            onClick={closeMenuThen(() => void handleAddPhase(true))}
          />
        )}
        {canAddIntake && (
          <AddMenuItem
            icon={<ClipboardList size={18} />}
            primary="Add Intake Form"
            secondary="Create a new intake form"
            onClick={closeMenuThen(onAddIntake)}
          />
        )}
        {canAddIntake && (
          <AddMenuItem
            icon={<Link2 size={18} />}
            primary="Link Intake Form"
            secondary="Attach an existing form"
            onClick={closeMenuThen(onLinkIntake)}
          />
        )}
      </Menu>
    </>
  );
}

interface AddMenuItemProps {
  icon: React.ReactNode;
  onClick: () => void;
  primary: string;
  secondary: string;
  dataCy?: string;
}

function AddMenuItem({ dataCy, icon, onClick, primary, secondary }: AddMenuItemProps) {
  const { classes } = usePhasesHeaderStyles();
  return (
    <MenuItem dense data-cy={dataCy} onClick={onClick}>
      <ListItemIcon className={classes.menuIcon}>{icon}</ListItemIcon>
      <ListItemText
        classes={{ primary: classes.menuPrimary, secondary: classes.menuSecondary }}
        primary={primary}
        secondary={secondary}
      />
    </MenuItem>
  );
}
